// The Hand's controller (docs/04 §21): while autospin is on, it asks the pure policy in sim/hand.ts
// what to do next, at the pace H_SPEED allows, and lets go when a stop condition fires.
import { computeModifiers } from '../sim/modifiers';
import { handInterval, handShouldStop, handStep, handUnlocked } from '../sim/hand';
import { t } from '../i18n/t';
import { dispatch, getGame, getUI, onEvents, toast } from '../state/store';
import type { EventPlayer } from './timeline/EventPlayer';
import type { MachineScene } from './machine/MachineScene';

export class HandController {
  private last = 0;
  private stopping = false;

  constructor(
    private player: EventPlayer,
    private machine: MachineScene,
  ) {
    setInterval(() => this.tick(), 50);
    onEvents((events) => {
      const g = getGame();
      if (!g.meta.hand.autospin || this.stopping) return;
      if (handShouldStop(events, g.meta.hand, computeModifiers(g.meta, g.run))) this.stop();
    });
  }

  toggle(): void {
    const g = getGame();
    if (!handUnlocked(computeModifiers(g.meta, g.run))) return;
    if (g.meta.hand.autospin) this.stop();
    else {
      dispatch({ t: 'SET_HAND', hand: { autospin: true } });
      this.last = 0;
      void this.machine.lever.setHand(true);
    }
  }

  stop(): void {
    if (!getGame().meta.hand.autospin) return;
    this.stopping = true;
    dispatch({ t: 'SET_HAND', hand: { autospin: false } });
    void this.machine.lever.setHand(false);
    toast(t('ui.hand.stopped'));
    this.stopping = false;
  }

  private tick(): void {
    const g = getGame();
    const ui = getUI();
    // The machine (and its lever) is only built once a run exists.
    if (!this.machine.lever) return;
    if (!g.meta.hand.autospin) {
      if (this.machine.lever.handOn) void this.machine.lever.setHand(false);
      return;
    }
    if (ui.screen !== 'machine' || ui.stage === 'waking' || ui.panel || this.player.busy) return;
    const mods = computeModifiers(g.meta, g.run);
    if (!handUnlocked(mods)) return this.stop();
    if (!this.machine.lever.handOn) void this.machine.lever.setHand(true);
    const now = performance.now();
    if (now - this.last < handInterval(mods) * 1000) return;
    const cmd = handStep(g, mods);
    if (!cmd) return this.stop();
    this.last = now;
    if (cmd.t === 'PULL') this.player.leverPulled = false;
    if (!dispatch(cmd)) this.stop();
  }
}
